import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { LOGO_URL, PHONE_DISPLAY, EMAIL, waUrl } from "../lib/constants";

const GOLD = "#c9a227";
const BLACK = "#0a0a0a";

/**
 * /thank-you — Post-booking confirmation page.
 * Calendly redirects here after a successful booking.
 * Fires Meta Pixel Purchase event on page load using event params from Calendly.
 * URL example: /thank-you?invitee_full_name=John&invitee_email=...&event_type_name=...
 * Compliance: no "therapy"/"clinical" anywhere.
 */
const ThankYou = () => {
    const firedRef = useRef(false);
    const [needsSlotBooking, setNeedsSlotBooking] = React.useState(false);

    useEffect(() => {
        document.title = "Booking Confirmed — The Reset Sanctuary";

        // Idempotency guard — React 18 StrictMode double-invokes useEffect in dev.
        // Without this, Purchase fires twice with mismatched event_ids → Meta dedup fails.
        if (firedRef.current) return;
        firedRef.current = true;

        // Read price from Calendly URL params if present (Calendly sends ?invitee_payment_amount=29.99)
        // OR from Stripe redirect (?sku=bundle → $99, ?sku=membership → $79, ?sku=single → $29.99 pay-first)
        const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
        const skuParam = params.get("sku");
        const SKU_PRICE_MAP = {
            single: { value: 29.99, content_name: "Sleep & Anxiety Broadcast", content_id: "sleep-anxiety-broadcast-single" },
            bundle: { value: 99, content_name: "5-Seat Corporate Pass", content_id: "trs-bundle-99" },
            membership: { value: 79, content_name: "Unlimited Membership", content_id: "trs-membership-79" },
        };
        const skuPreset = skuParam && SKU_PRICE_MAP[skuParam.toLowerCase()];

        // Detect flow: pay-first (direct Stripe, no Calendly slot yet) vs Calendly-first (slot already booked)
        const hasCalendlyData = !!(params.get("invitee_email") || params.get("invitee_full_name") || params.get("event_type_name"));
        const isPayFirst = (skuParam || "").toLowerCase() === "single" && !hasCalendlyData;
        setNeedsSlotBooking(isPayFirst);

        const rawAmount = params.get("invitee_payment_amount") || params.get("value");
        const purchaseValue = skuPreset
            ? skuPreset.value
            : (Number(rawAmount) > 0 ? Number(rawAmount) : 29.99);
        const purchaseCurrency = (params.get("invitee_payment_currency") || params.get("currency") || "USD").toUpperCase();
        const purchaseContentName = skuPreset ? skuPreset.content_name : "Sleep & Anxiety Broadcast";
        const purchaseContentId = skuPreset ? skuPreset.content_id : "sleep-anxiety-broadcast-single";
        const eventId = `purchase-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

        // Fire Meta Pixel Purchase event (Andromeda-friendly — numeric value, deduplicated by event_id)
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
            try {
                window.fbq("track", "Purchase", {
                    value: purchaseValue,
                    currency: purchaseCurrency,
                    content_name: purchaseContentName,
                    content_category: "Live Broadcast",
                    content_ids: [purchaseContentId],
                    content_type: "product",
                    num_items: 1,
                    eventID: eventId,
                });
            } catch (e) {
                // silent — never break the page if Pixel fails
            }
        }

        // Server-side CAPI mirror (deduplicated by event_id)
        const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
        if (API_BASE) {
            const getCookie = (n) => {
                const v = document.cookie.match("(^|;)\\s*" + n + "\\s*=\\s*([^;]+)");
                return v ? v.pop() : null;
            };
            try {
                fetch(`${API_BASE}/api/meta/capi`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        event_name: "Purchase",
                        event_source_url: window.location.href,
                        event_id: eventId,
                        email: params.get("invitee_email") || null,
                        first_name: (params.get("invitee_full_name") || "").split(" ")[0] || null,
                        fbp: getCookie("_fbp"),
                        fbc: getCookie("_fbc"),
                        client_user_agent: navigator.userAgent,
                        value: purchaseValue,
                        currency: purchaseCurrency,
                        content_name: purchaseContentName,
                    }),
                    keepalive: true,
                }).catch(() => {});
            } catch (e) {}
        }
    }, []);

    return (
        <main
            data-testid="thank-you-page"
            style={{
                minHeight: "100vh",
                backgroundColor: BLACK,
                color: "#fff",
                fontFamily: "Inter, sans-serif",
                padding: "80px 24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <div style={{
                maxWidth: 640,
                width: "100%",
                textAlign: "center",
                border: "1px solid rgba(201,162,39,0.2)",
                padding: "60px 40px",
                backgroundColor: "#0d0d0d",
                position: "relative",
            }}>
                <img src={LOGO_URL} alt="The Reset Sanctuary" style={{ width: 72, margin: "0 auto 28px", display: "block" }} />

                <p style={{
                    color: GOLD, fontSize: 11, letterSpacing: "0.34em",
                    textTransform: "uppercase", fontWeight: 600, margin: "0 0 14px",
                }} data-testid="ty-eyebrow">
                    Booking Confirmed
                </p>

                <h1 className="font-serif" style={{
                    fontSize: "clamp(28px, 5vw, 40px)", fontWeight: 400,
                    margin: "0 0 18px", lineHeight: 1.15, letterSpacing: "-0.015em",
                    color: "#fff",
                }} data-testid="ty-headline">
                    {needsSlotBooking ? (
                        <>Payment received. <span style={{ color: GOLD, fontStyle: "italic" }}>Now pick your session.</span></>
                    ) : (
                        <>You're in. <span style={{ color: GOLD, fontStyle: "italic" }}>See you on the inside.</span></>
                    )}
                </h1>

                <p style={{
                    color: "#ccc", fontSize: 15, lineHeight: 1.7,
                    margin: "0 auto 32px", maxWidth: 520, fontWeight: 300,
                }} data-testid="ty-intro">
                    {needsSlotBooking
                        ? "You've paid $29.99 for the Sleep & Anxiety Broadcast — one final step. Pick the session that works for you. Sessions run Tue / Thu / Fri in two global time blocks. You can join as many as you want with this booking."
                        : "Your spot is locked. Check your inbox now — Calendly has sent you the Zoom link, the session time, and a calendar invite. Save them. Show up."}
                </p>

                {needsSlotBooking && (
                    <div data-testid="ty-pick-session-block" style={{ margin: "0 auto 36px", maxWidth: 520 }}>
                        <p style={{ color: GOLD, fontSize: 10.5, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 600, margin: "0 0 14px", textAlign: "left" }}>
                            Choose your time block
                        </p>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
                            <a
                                href="https://calendly.com/iamtatvak/sleep-anxiety-broadcast-block-a-euro-gulf"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="ty-pick-block-a"
                                style={{
                                    display: "block", padding: "16px 18px",
                                    border: `1px solid ${GOLD}`, backgroundColor: GOLD,
                                    color: BLACK, textDecoration: "none",
                                    fontSize: 13, letterSpacing: "0.18em",
                                    textTransform: "uppercase", fontWeight: 700,
                                    textAlign: "center",
                                }}
                            >
                                Block A · 9:30 PM Dubai / 6:30 PM London →
                            </a>
                            <a
                                href="https://calendly.com/iamtatvak/sleep-anxiety-broadcast-block-a-euro-gulf-clone"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="ty-pick-block-b"
                                style={{
                                    display: "block", padding: "16px 18px",
                                    border: `1px solid ${GOLD}`, backgroundColor: "transparent",
                                    color: GOLD, textDecoration: "none",
                                    fontSize: 13, letterSpacing: "0.18em",
                                    textTransform: "uppercase", fontWeight: 600,
                                    textAlign: "center",
                                }}
                            >
                                Block B · 1 AM Dubai / 5 PM NY →
                            </a>
                        </div>
                        <p style={{ color: "#888", fontSize: 11.5, lineHeight: 1.6, margin: "12px 0 0", textAlign: "center", fontWeight: 300 }}>
                            Pick whichever fits your timezone. Calendly will email you the Zoom link.
                        </p>
                    </div>
                )}

                <hr style={{
                    height: 1, border: 0,
                    background: "linear-gradient(90deg, transparent, rgba(201,162,39,0.4), transparent)",
                    maxWidth: 280, margin: "28px auto",
                }} />

                <div style={{ textAlign: "left", maxWidth: 480, margin: "0 auto" }}>
                    <p style={{
                        color: GOLD, fontSize: 10.5, letterSpacing: "0.3em",
                        textTransform: "uppercase", fontWeight: 600, margin: "0 0 14px",
                    }}>
                        What to do before the session
                    </p>

                    <ol style={{ paddingLeft: 18, margin: "0 0 24px" }} data-testid="ty-prep-list">
                        <li style={{ color: "#ddd", fontSize: 14, lineHeight: 1.7, marginBottom: 10, fontWeight: 300 }}>
                            <strong style={{ color: "#fff" }}>Quality headphones.</strong> Wired if you have them. No phone speakers — the frequencies need to reach you properly.
                        </li>
                        <li style={{ color: "#ddd", fontSize: 14, lineHeight: 1.7, marginBottom: 10, fontWeight: 300 }}>
                            <strong style={{ color: "#fff" }}>Your bed, dim light.</strong> Lying down. Your camera and mic mute automatically on entry. No multitasking.
                        </li>
                        <li style={{ color: "#ddd", fontSize: 14, lineHeight: 1.7, marginBottom: 10, fontWeight: 300 }}>
                            <strong style={{ color: "#fff" }}>Phone on Do Not Disturb.</strong> 60 minutes uninterrupted. Same rule as a flight.
                        </li>
                        <li style={{ color: "#ddd", fontSize: 14, lineHeight: 1.7, marginBottom: 10, fontWeight: 300 }}>
                            <strong style={{ color: "#fff" }}>Hydrated, light dinner.</strong> Heavy food fights the brain's shift into evening decompression.
                        </li>
                        <li style={{ color: "#ddd", fontSize: 14, lineHeight: 1.7, marginBottom: 0, fontWeight: 300 }}>
                            <strong style={{ color: "#fff" }}>Arrive 5 min early.</strong> Headphones already on, lying down, ready.
                        </li>
                    </ol>
                </div>

                <hr style={{
                    height: 1, border: 0,
                    background: "linear-gradient(90deg, transparent, rgba(201,162,39,0.4), transparent)",
                    maxWidth: 280, margin: "28px auto",
                }} />

                <p style={{
                    color: "#aaa", fontSize: 13, lineHeight: 1.7,
                    margin: "0 auto 24px", maxWidth: 480, fontWeight: 300, fontStyle: "italic",
                }}>
                    Most people fall asleep in the chair. That's the work happening. Let it.
                </p>

                <div style={{
                    display: "flex", gap: 12, flexWrap: "wrap",
                    justifyContent: "center", marginTop: 12,
                }}>
                    <a
                        href={waUrl("Hi, I just booked a live session. Quick question —")}
                        data-testid="ty-whatsapp-cta"
                        style={{
                            display: "inline-block",
                            padding: "12px 24px",
                            border: `1px solid ${GOLD}`,
                            color: GOLD,
                            textDecoration: "none",
                            fontSize: 11.5,
                            letterSpacing: "0.22em",
                            textTransform: "uppercase",
                            fontWeight: 600,
                        }}
                    >
                        WhatsApp Us
                    </a>
                    <Link
                        to="/"
                        data-testid="ty-home-cta"
                        style={{
                            display: "inline-block",
                            padding: "12px 24px",
                            border: "1px solid rgba(255,255,255,0.2)",
                            color: "#fff",
                            textDecoration: "none",
                            fontSize: 11.5,
                            letterSpacing: "0.22em",
                            textTransform: "uppercase",
                            fontWeight: 500,
                        }}
                    >
                        Back to Site
                    </Link>
                </div>

                <p style={{
                    color: "#666", fontSize: 10.5, letterSpacing: "0.22em",
                    textTransform: "uppercase", margin: "40px 0 0", fontWeight: 500,
                }}>
                    {PHONE_DISPLAY} · {EMAIL}
                </p>
            </div>
        </main>
    );
};

export default ThankYou;
